import React, { useMemo } from 'react';

import { Button, Tooltip, Typography } from '@mui/material';
import _ from 'lodash';

import { ReactComponent as CheckmarkIconWhite } from '../../../../common/assets/icons/checkmark_icon_white.svg';
import { ReactComponent as WarningIconWhite } from '../../../../common/assets/icons/warning_icon_white.svg';
import {
  CodeInvalidReason,
  SelectableCode,
} from '../../../../common/types/Codes';

type UseButtonProps = {
  selectCode: (code: SelectableCode) => void;
  code: SelectableCode;
  hasMultiplePrimaryCodes: boolean;
};

export const UseButton: React.FC<UseButtonProps> = ({
  selectCode,
  code,
  hasMultiplePrimaryCodes,
}) => {
  const getPrimaryCodesError = () => {
    return hasMultiplePrimaryCodes ? 'Multiple E&M codes detected.' : '';
  };

  const getInvalidComboString = (invalidCombos: CodeInvalidReason[]) => {
    return invalidCombos
      .map((reason) => `${reason.code}: ${reason.reason}`)
      .join('\n');
  };

  const codeState = useMemo(() => {
    if (code.selected && !_.isEmpty(code.invalidCombos)) return 'error';

    if (code.selected && code.isPrimaryCode && hasMultiplePrimaryCodes)
      return 'error';

    if (code.selected) return 'good';

    return 'none';
  }, [
    code.selected,
    code.invalidCombos,
    code.isPrimaryCode,
    hasMultiplePrimaryCodes,
  ]);

  return (
    <>
      {codeState === 'error' ? (
        <Tooltip
          enterDelay={500}
          disableFocusListener
          disableTouchListener
          sx={{
            backgroundColor: 'rgba(253, 240, 240, 1)',
          }}
          title={
            <span style={{ whiteSpace: 'pre-line' }}>
              {getPrimaryCodesError()}
              {getInvalidComboString(code.invalidCombos ?? [])}
            </span>
          }
        >
          <Button
            sx={{
              minWith: '140px',
              borderRadius: '40px',
              flexShrink: 0,
              backgroundColor: 'error.main',
              minWidth: '90px',
            }}
            onClick={() => selectCode(code)}
            color='darkBlue'
            variant='contained'
            disableElevation
          >
            <WarningIconWhite style={{ flexShrink: 0 }} />
            <Typography color='#fff' fontWeight={500}>
              Use
            </Typography>
          </Button>
        </Tooltip>
      ) : (
        <Button
          sx={{
            minWith: '140px',
            borderRadius: '40px',
            flexShrink: 0,
            backgroundColor:
              codeState === 'good' ? 'darkBlue.main' : 'rgba(82, 86, 98, 0.60)',
            minWidth: '90px',
          }}
          onClick={() => selectCode(code)}
          color='darkBlue'
          variant='contained'
          disableElevation
        >
          {codeState === 'good' && (
            <CheckmarkIconWhite style={{ flexShrink: 0 }} />
          )}
          <Typography color='#fff' fontWeight={500}>
            Use
          </Typography>
        </Button>
      )}
    </>
  );
};
